import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import ChartCard from './ChartCard';
import { fmtCurrency, fmtNumber, exportRowsToCsv } from './chartTheme';
import { colorOfTableCategory } from './sourceGroups';

const SOURCE_LABELS = {
  organization: 'Organization',
  referral: 'Referral',
  events: 'Event',
  website: 'Website',
  manual: 'Manual / Other',
};

// Fixed category layer over the per-partner rows (Nic 2026-08-24): the
// default view shows one aggregate row per category instead of every partner
// and event; Events and Partners click through to their full constituent
// lists. Envoy is our named organization feed, Referral Program is ONLY the
// umbrella (referrals from people who are not partners), Website is the
// /reach-out self-lead page, and Manual / Other is a single bucket - nothing
// beneath any of those four. Every other
// partner - individual referrer-partners AND future non-Envoy organizations -
// lands under Partners. categoryOf pins Envoy and the umbrella by name
// (fixed rules; revisit only if either partner is renamed).
const CATEGORIES = [
  {
    key: 'envoy',
    label: 'Envoy',
    sourceSystem: 'organization',
    drillable: false,
    noun: null,
  },
  {
    key: 'referral',
    label: 'Referral Program',
    sourceSystem: 'referral',
    drillable: false,
    noun: null,
  },
  {
    key: 'partners',
    label: 'Partners',
    sourceSystem: 'organization',
    drillable: true,
    noun: 'partners',
  },
  {
    key: 'events',
    label: 'Events',
    sourceSystem: 'events',
    drillable: true,
    noun: 'events',
  },
  {
    key: 'website',
    label: 'Website',
    sourceSystem: 'website',
    drillable: false,
    noun: null,
  },
  {
    key: 'manual',
    label: 'Manual / Other',
    sourceSystem: 'manual',
    drillable: false,
    noun: null,
  },
];

const categoryOf = (s) => {
  if (s.sourceSystem === 'events') return 'events';
  if (s.sourceSystem === 'website') return 'website';
  if (s.sourceSystem === 'manual') return 'manual';
  if (s.sourceSystem === 'referral') {
    return s.label === 'Referral Program' ? 'referral' : 'partners';
  }
  return s.label === 'Envoy' ? 'envoy' : 'partners';
};

const BASE_COLUMNS = [
  { key: 'label', label: 'Source', numeric: false },
  { key: 'received', label: 'Received', numeric: true },
  { key: 'contacted', label: 'Contacted', numeric: true },
  { key: 'invited', label: 'Invited', numeric: true },
  { key: 'accounts', label: 'Accounts', numeric: true },
  { key: 'saSigned', label: 'SA Signed', numeric: true },
  { key: 'conversionPct', label: 'Conv %', numeric: true },
];

// The dollar columns follow the payload: the server strips invoicedAmount/
// paidAmount for viewers without billing:funnel_financials (same gate as the
// KPI tiles and funnel amounts), so their presence drives the table AND the
// CSV export.
const MONEY_COLUMNS = [
  { key: 'invoicedAmount', label: 'Invoiced $', numeric: true },
  { key: 'paidAmount', label: 'Paid $', numeric: true },
];

const sortRows = (rows, sortKey, sortDir) => {
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (typeof av === 'string') return av.localeCompare(bv) * dir;
    return ((av || 0) - (bv || 0)) * dir;
  });
};

// Source performance across the whole journey, sortable by any column.
// Category view by default; drill = the category key whose constituents are
// being listed (full list, scrollable - no top-N cap, no CSV punting).
const SourceTable = ({ sources = [], caption, filenameSuffix, loading }) => {
  const [sortKey, setSortKey] = useState('received');
  const [sortDir, setSortDir] = useState('desc');
  const [drill, setDrill] = useState(null);

  const showFinancials = sources.some(
    (s) => s.invoicedAmount !== undefined || s.paidAmount !== undefined,
  );
  const columns = showFinancials
    ? [...BASE_COLUMNS, ...MONEY_COLUMNS]
    : BASE_COLUMNS;

  const grouped = useMemo(() => {
    const m = new Map();
    for (const s of sources) {
      const c = categoryOf(s);
      if (!m.has(c)) m.set(c, []);
      m.get(c).push(s);
    }
    return m;
  }, [sources]);

  // With the toolbar already filtered to one class, the category layer is
  // redundant - show the flat list directly.
  const multiCategory = grouped.size > 1;
  const activeDrill =
    multiCategory && drill && grouped.has(drill) ? drill : null;
  const drillMeta = activeDrill
    ? CATEGORIES.find((c) => c.key === activeDrill)
    : null;

  // A drill is a transient view of the CURRENT cohort: when a toolbar filter
  // or range change pulls the drilled category out from under it, clear the
  // state entirely - otherwise a later cohort that re-includes the category
  // would silently resurrect the old drill view.
  useEffect(() => {
    if (drill && (!multiCategory || !grouped.has(drill))) setDrill(null);
  }, [drill, multiCategory, grouped]);

  const viewRows = useMemo(() => {
    if (!multiCategory) return sources;
    if (activeDrill) return grouped.get(activeDrill) || [];
    return CATEGORIES.filter((c) => grouped.has(c.key)).map((c) => {
      const kids = grouped.get(c.key);
      const sum = (key) => kids.reduce((acc, s) => acc + (s[key] || 0), 0);
      const received = sum('received');
      const clickable = c.drillable;
      return {
        key: `cat:${c.key}`,
        category: c.key,
        clickable,
        childCount: kids.length,
        noun: c.noun,
        label: c.label,
        sourceSystem: c.sourceSystem,
        received,
        contacted: sum('contacted'),
        invited: sum('invited'),
        accounts: sum('accounts'),
        saSigned: sum('saSigned'),
        ...(showFinancials
          ? {
              invoicedAmount: sum('invoicedAmount'),
              paidAmount: sum('paidAmount'),
            }
          : {}),
        conversionPct: received ? sum('accounts') / received : 0,
      };
    });
  }, [sources, grouped, multiCategory, activeDrill, showFinancials]);

  const displayRows = useMemo(
    () => sortRows(viewRows, sortKey, sortDir),
    [viewRows, sortKey, sortDir],
  );

  // The CSV always carries the full per-partner breakdown regardless of the
  // on-screen view.
  const exportRows = useMemo(
    () => sortRows(sources, sortKey, sortDir),
    [sources, sortKey, sortDir],
  );

  // Category rows aggregate several partners; scrolling only matters on the
  // longer constituent / flat lists.
  const scrollable = !multiCategory || Boolean(activeDrill);

  const toggleSort = (key, numeric) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(numeric ? 'desc' : 'asc');
    }
  };

  const handleExport = () =>
    exportRowsToCsv(
      `leads-sources_${filenameSuffix}`,
      exportRows.map((s) => ({
        Source: s.label,
        Type: SOURCE_LABELS[s.sourceSystem] || s.sourceSystem,
        Received: s.received,
        Contacted: s.contacted,
        Invited: s.invited,
        Accounts: s.accounts,
        'SA Signed': s.saSigned,
        'Conversion %': `${Math.round((s.conversionPct || 0) * 100)}%`,
        ...(showFinancials
          ? { 'Invoiced $': s.invoicedAmount, 'Paid $': s.paidAmount }
          : {}),
      })),
    );

  return (
    <ChartCard
      title="Source performance"
      caption={caption}
      onExport={handleExport}
    >
      {loading ? (
        <div className="h-40 animate-pulse rounded-md bg-muted" />
      ) : sources.length === 0 ? (
        <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
          No leads received in this period.
        </div>
      ) : (
        <>
          {drillMeta ? (
            <div className="mb-2 flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setDrill(null)}
                className="inline-flex items-center gap-0.5 font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                All sources
              </button>
              <span className="text-muted-foreground">/</span>
              <span className="font-medium text-foreground">
                {drillMeta.label}
              </span>
            </div>
          ) : null}
          <div
            className={cn(
              'overflow-x-auto rounded-lg border border-border',
              scrollable && 'max-h-96 overflow-y-auto',
            )}
          >
            <table className="w-full text-sm">
              <thead>
                {/* bg-muted, NOT bg-secondary — Bootstrap's global .bg-secondary
                    (!important, dark grey) hijacks that class on this branch.
                    Solid bg + sticky so the header holds while the constituent
                    lists scroll. */}
                <tr className="border-b border-border">
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className={cn(
                        'sticky top-0 z-10 bg-muted px-3 py-2.5 text-xs font-medium uppercase tracking-wide text-muted-foreground',
                        c.numeric ? 'text-right' : 'text-left',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(c.key, c.numeric)}
                        className={cn(
                          'inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-foreground',
                          sortKey === c.key && 'text-foreground',
                        )}
                      >
                        {c.label}
                        {sortKey === c.key ? (
                          sortDir === 'asc' ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          )
                        ) : null}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.map((s) => (
                  <tr
                    key={s.key}
                    onClick={
                      s.clickable ? () => setDrill(s.category) : undefined
                    }
                    onKeyDown={
                      s.clickable
                        ? (e) => {
                            if (e.key === 'Enter') setDrill(s.category);
                          }
                        : undefined
                    }
                    role={s.clickable ? 'button' : undefined}
                    tabIndex={s.clickable ? 0 : undefined}
                    className={cn(
                      'border-b border-border last:border-b-0 hover:bg-accent/50 dark:hover:bg-surface-elevated',
                      s.clickable && 'cursor-pointer',
                    )}
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1 font-medium text-foreground">
                        {s.label}
                        {s.clickable ? (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : null}
                      </div>
                      {/* Each group's dot wears the same color as its bar
                          in "Where they came from" (Nic 2026-10-25). */}
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ background: colorOfTableCategory(s.category || categoryOf(s)) }}
                        />
                        {s.clickable && s.noun
                          ? `${s.childCount} ${s.noun}`
                          : SOURCE_LABELS[s.sourceSystem] || s.sourceSystem}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium text-foreground">
                      {fmtNumber(s.received)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {fmtNumber(s.contacted)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {fmtNumber(s.invited)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {fmtNumber(s.accounts)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {fmtNumber(s.saSigned)}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-1.5 w-14 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-chart-1"
                            style={{
                              width: `${Math.min(100, Math.round((s.conversionPct || 0) * 100))}%`,
                            }}
                          />
                        </div>
                        <span className="w-9 text-right text-foreground">
                          {Math.round((s.conversionPct || 0) * 100)}%
                        </span>
                      </div>
                    </td>
                    {showFinancials ? (
                      <>
                        <td className="px-3 py-2.5 text-right">
                          {fmtCurrency(s.invoicedAmount)}
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-status-success-fg">
                          {fmtCurrency(s.paidAmount)}
                        </td>
                      </>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </ChartCard>
  );
};

export default SourceTable;
